import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const modules = [
  { 
    id: 1, 
    title: 'FOUNDATIONS', 
    episodes: 6, 
    desc: 'Master the absolute basics of market structure, candlesticks, and asset classes.',
    img: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&q=80&w=800'
  },
  { 
    id: 2, 
    title: 'CHART READING', 
    episodes: 5, 
    desc: 'Identify support, resistance, and supply/demand zones like an institutional trader.',
    img: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&q=80&w=800'
  },
  { 
    id: 3, 
    title: 'TRADE INTELLIGENCE', 
    episodes: 4, 
    desc: 'Read volume, heatmaps, and option chains to uncover smart money positioning.',
    img: 'https://images.unsplash.com/photo-1642543492481-44e81e391452?auto=format&fit=crop&q=80&w=800'
  },
  { 
    id: 4, 
    title: 'TRADE EXECUTION', 
    episodes: 1, 
    desc: 'Precision entries, sniper targets, and iron-clad risk management rules.',
    img: 'https://images.unsplash.com/photo-1535320903710-d9938a44917a?auto=format&fit=crop&q=80&w=800'
  },
  { 
    id: 5, 
    title: 'ROCKETALGO SYSTEM', 
    episodes: 4, 
    desc: 'The proprietary ABCMP formula for filtering noise and taking only high-conviction setups.',
    img: 'https://images.unsplash.com/photo-1605792657660-596af9009e82?auto=format&fit=crop&q=80&w=800'
  }
];

const ModuleRow = () => {
  const rowRef = useRef(null);

  const scroll = (direction) => {
    if (rowRef.current) {
      const { scrollLeft, clientWidth } = rowRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth * 0.8 : scrollLeft + clientWidth * 0.8;
      rowRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  return (
    <div className="mb-14 relative group z-10 w-full">
      <div className="px-4 md:px-12 mb-4">
        <h3 className="text-[18px] md:text-[22px] font-bold text-white tracking-wide">
          Start Your Trading Journey
        </h3>
        <p className="text-sm text-gray-400 mt-1">Begin from zero and progress toward high-conviction execution.</p>
      </div>

      <div className="relative">
        <button 
          onClick={() => scroll('left')} 
          className="hidden md:flex absolute left-0 top-0 bottom-0 w-12 bg-black/60 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 items-center justify-center hover:bg-black/80 hover:w-16 backdrop-blur-sm rounded-r-md"
        >
          <SafeIcon name="ChevronLeft" className="w-8 h-8 text-white transform group-hover:scale-125 transition-transform" />
        </button>

        <div 
          ref={rowRef} 
          className="flex space-x-4 overflow-x-auto hide-scrollbar px-4 md:px-12 pb-8 pt-2 scroll-smooth snap-x snap-mandatory"
        >
          {modules.map((mod) => (
            <motion.div
              key={mod.id}
              className="snap-start shrink-0 relative group/card w-[300px] md:w-[380px] aspect-[16/9] rounded-xl overflow-hidden cursor-pointer bg-dark-card border border-white/5 transition-all duration-500 hover:border-rocket/50 hover:shadow-[0_0_30px_rgba(255,102,0,0.2)] hover:z-50"
              whileHover={{ scale: 1.05 }}
            >
              <img 
                src={mod.img} 
                alt={mod.title} 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-110 opacity-70 group-hover/card:opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90" />
              
              {/* Default State */}
              <div className="absolute inset-0 p-5 flex flex-col justify-between transition-opacity duration-300">
                <div className="flex justify-between items-start">
                  <span className="bg-black/60 backdrop-blur-md border border-white/10 text-white text-xs font-bold px-2.5 py-1 rounded">
                    Module {mod.id}
                  </span>
                  <span className="text-rocket text-xs font-bold bg-rocket/10 px-2 py-1 rounded border border-rocket/20">
                    {mod.episodes} Episodes
                  </span>
                </div>
                <div>
                  <h4 className="text-xl font-black text-white tracking-tight drop-shadow-lg">{mod.title}</h4>
                </div>
              </div>

              {/* Hover State Detail Overlay */}
              <div className="absolute inset-0 p-5 bg-gradient-to-t from-black via-black/90 to-black/40 flex flex-col justify-end opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 translate-y-4 group-hover/card:translate-y-0">
                <p className="text-sm text-gray-300 mb-4 line-clamp-3 leading-relaxed">
                  {mod.desc}
                </p>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-rocket text-white hover:bg-rocket-hover transition-colors shadow-[0_0_15px_rgba(255,102,0,0.5)]">
                    <SafeIcon name="Play" className="w-5 h-5 ml-1 fill-current" />
                  </div>
                  <span className="text-sm font-bold text-white">Begin Watching</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <button 
          onClick={() => scroll('right')} 
          className="hidden md:flex absolute right-0 top-0 bottom-0 w-12 bg-black/60 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 items-center justify-center hover:bg-black/80 hover:w-16 backdrop-blur-sm rounded-l-md"
        >
          <SafeIcon name="ChevronRight" className="w-8 h-8 text-white transform group-hover:scale-125 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export default ModuleRow;